/*
 * EventSource wrapper for the AquaStar customer web app.
 *
 * Why a polyfill: the browser-native `EventSource` doesn't accept
 * custom headers, so it can't carry `Authorization: Bearer <jwt>`.
 * The Gateway accepts `?access_token=` as a fallback but logs a
 * warning every time, and querystring tokens leak via server access
 * logs. `event-source-polyfill` ships a header-aware EventSource that
 * matches the iOS client's auth path.
 *
 * Reconnect: the polyfill auto-reconnects on transport drops with a
 * configurable `heartbeatTimeout`. We layer our own retry-with-backoff
 * on top for hard failures (401, server-side close).
 *
 * See:
 *   - Docs/architecture/ARCH_WEB_USER_APP.md §5
 *   - Gateway/local-gateway/src/routes/app-streams.ts (the endpoint)
 *   - device-translator/runtime.ts L412/L549 (event shapes)
 */

import { EventSourcePolyfill } from 'event-source-polyfill';

import { GATEWAY_BASE, APP_ID, appStreamUrl } from './config';
import { getToken } from './apiClient';

/** Event types the AquaStar `pumpState` stream emits. */
export type PumpStateEventType =
  | 'state'
  | 'pump_online'
  | 'pump_offline'
  | 'pump_first_connected';

/** Common envelope shape. Producer-side at runtime.ts L414/L550. */
export interface PumpStateEvent<T = unknown> {
  /** Server-side `eventType` slug — same value as the SSE event name. */
  type: PumpStateEventType;
  /**
   * Per-event payload. For `state`, includes `{ deviceId, mac, reported,
   * malformed, timestamp }`. For presence events, `{ deviceId, mac,
   * status, firstConnect, timestamp }`.
   */
  payload: T;
  /** Wall-clock from the producer; ISO-8601. */
  timestamp?: string;
}

export interface PumpStateConnection {
  /** Close the EventSource and stop the reconnect loop. */
  close: () => void;
}

interface OpenOptions {
  /**
   * Restrict delivery to a single `deviceId`. Subscribers without it
   * see events for every pump in their owned + followed set.
   */
  deviceId?: string;
  /** Called once when the stream is open. */
  onOpen?: () => void;
  /** Called for every event, after type-specific handlers. */
  onEvent?: (event: PumpStateEvent) => void;
  /** Called on transport-level errors (auto-reconnect handles most). */
  onError?: (err: unknown) => void;
}

/**
 * Open the AquaStar `pumpState` stream. Returns a handle the caller
 * uses to close the connection on unmount.
 *
 * Re-issues a fresh EventSource on hard failures (401 = expired JWT,
 * server-side disconnect with no heartbeat). Backoff: 1s → 30s, with
 * jitter to avoid thundering-herd on a server bounce.
 */
export function openPumpStateStream(
  opts: OpenOptions = {},
): PumpStateConnection {
  let closed = false;
  let es: InstanceType<typeof EventSourcePolyfill> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let backoff = 1000;

  function scheduleReconnect() {
    if (closed) return;
    if (reconnectTimer) return;
    const jitter = Math.random() * 250;
    const delay = Math.min(backoff, 30_000) + jitter;
    backoff = Math.min(backoff * 2, 30_000);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      open();
    }, delay);
  }

  function open() {
    if (closed) return;
    if (es) {
      try {
        es.close();
      } catch {
        /* swallow */
      }
      es = null;
    }

    const token = getToken();
    if (!token) {
      // No auth — let the caller decide whether to retry once they
      // recover a session. Don't churn-loop hammering the endpoint.
      opts.onError?.(new Error('No access token; SSE not opened.'));
      return;
    }

    const url = new URL(appStreamUrl('pumpState'), window.location.origin);
    if (opts.deviceId) {
      url.searchParams.set('deviceId', opts.deviceId);
    }

    es = new EventSourcePolyfill(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
      // The Gateway sends a `:keepalive\n\n` comment every 30s
      // (configurable per stream declaration). 45s gives one missed
      // heartbeat of slack before we treat the connection as dead.
      heartbeatTimeout: 45_000,
    });

    es.onopen = () => {
      backoff = 1000; // reset on every successful open
      opts.onOpen?.();
    };

    es.onerror = (err: unknown) => {
      // Polyfill emits onerror for both transient blips and hard
      // closes. Best signal we have is the readyState — if it's CLOSED,
      // the polyfill won't reconnect itself. We also force-reconnect
      // after onerror to recover from 401s where the polyfill may keep
      // a dead socket open.
      const dead = !es || (es as { readyState?: number }).readyState === 2; // CLOSED
      opts.onError?.(err);
      if (dead || !closed) {
        try {
          es?.close();
        } catch {
          /* swallow */
        }
        es = null;
        scheduleReconnect();
      }
    };

    // Wire up known event types. The Gateway dispatches events using
    // the producer's `event` field as the SSE event name; native
    // EventSource only fires the matching listener (no fall-through to
    // `onmessage`), so we register them explicitly.
    const types: PumpStateEventType[] = [
      'state',
      'pump_online',
      'pump_offline',
      'pump_first_connected',
    ];
    for (const type of types) {
      // The polyfill exposes `addEventListener(type, listener)` but
      // ships its own `EventListener` typing that doesn't quite match
      // the DOM lib's. Cast through `unknown` so tsc lets us pass our
      // (Event)=>void handler — it's the right shape at runtime.
      const handler = (ev: Event) => {
        const me = ev as MessageEvent;
        let payload: unknown = null;
        try {
          payload = me.data ? JSON.parse(String(me.data)) : null;
        } catch {
          payload = me.data;
        }
        opts.onEvent?.({
          type,
          payload,
          timestamp: (payload as { timestamp?: string } | null)?.timestamp,
        });
      };
      (es as unknown as {
        addEventListener: (t: string, h: (ev: Event) => void) => void;
      }).addEventListener(type, handler);
    }
  }

  open();

  return {
    close() {
      closed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      try {
        es?.close();
      } catch {
        /* swallow */
      }
      es = null;
    },
  };
}

/** Re-export the canonical URL helper for callers that want it. */
export { appStreamUrl };

// Surface `GATEWAY_BASE` + `APP_ID` so the dev console can poke at the
// raw stream during debugging without re-importing config.
export const __debug = { GATEWAY_BASE, APP_ID };
